// BookOne corner widget — custom-element bundle.
//
// Customers paste two lines on their site:
//   <bookone-widget venue-id="<uuid>" theme="<preset>"></bookone-widget>
//   <script src="https://www.bookoneparty.com/widget.js" async></script>
//
// What this does: registers a <bookone-widget> custom element. On connect, it
// mounts a React tree (FloatingWidget + CateringWidget) directly in the host
// page's light DOM — no iframe. That means the conversation runs at the host
// origin and getUserMedia prompts fire from the host's own permission state.
// No Permissions-Policy header, no meta tag.
//
// Two paths:
//  - Fast: all metadata supplied as attributes (agent-id, venue-name) — renders
//    immediately. Settings page generates this form.
//  - Slow: only venue-id + theme — render a placeholder pill, fetch
//    /venues/:id/profile, then upgrade to the real widget.
import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { v7 as uuidv7 } from 'uuid';
import { FloatingWidget } from '@/components/floating-widget';
import {
  BRAND_PRESETS,
  BRAND_TRIGGER_CONFIG,
  BRAND_COPY,
  isBrandPreset,
  type BrandPreset,
  type TriggerConfig,
  type CopyConfig,
} from '@/lib/widget-brand-themes';
import type { CateringWidgetTheme } from '@/components/catering-widget';
import { SUPPORTED_LANGS, type SupportedLang } from '@/lib/widget-i18n';

// Normalize a raw language list (comma-split `languages` attribute or the
// venue's catering_config.widgetLanguages JSON array) into SupportedLang[].
// Accepts every platform language — the old inline filter only knew en|fr,
// silently dropping es/it for venues that enable them.
function normalizeLanguageList(raw: unknown): SupportedLang[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const langs = raw
    .map((s) => (typeof s === 'string' ? s.trim().toLowerCase() : ''))
    .filter((s): s is SupportedLang => (SUPPORTED_LANGS as readonly string[]).includes(s));
  return langs.length > 0 ? langs : undefined;
}

// API base baked at build time. Vercel sets this per-environment so the staging
// bundle hits staging and the prod bundle hits prod. Empty fallback turns into
// a same-origin call which is wrong on a foreign host — flag in a console.warn
// so a misbuilt bundle is loud.
const API_BASE: string =
  (process.env.NEXT_PUBLIC_API_URL ?? '').replace(/\/$/, '') ||
  'https://api.bookoneparty.com';

// BookOne origin — derived from the script tag that loaded this bundle. We need
// it to absolutize venue assets (logos, ambience audio, etc.) that are stored
// on bookoneparty.com. Without this, paths like '/membersonly-widget-logo.png'
// resolve against the host page (e.g. ui-two-topaz.vercel.app/membersonly-...)
// and 404. Iframe path didn't have this problem because the iframe ran at
// bookoneparty.com origin already; light-DOM custom-element runs at host origin.
const BOOKONE_ORIGIN: string = (() => {
  if (typeof document === 'undefined') return 'https://www.bookoneparty.com';
  const scripts = document.querySelectorAll<HTMLScriptElement>('script[src*="widget.js"]');
  const last = scripts[scripts.length - 1];
  if (last?.src) {
    try {
      return new URL(last.src).origin;
    } catch {}
  }
  return 'https://www.bookoneparty.com';
})();

function absolutize(path: string | null | undefined): string | null {
  if (!path) return null;
  if (/^[a-z]+:\/\//i.test(path)) return path; // already absolute (http, https, data, etc.)
  if (path.startsWith('//')) return path; // protocol-relative
  if (path.startsWith('/')) return BOOKONE_ORIGIN + path;
  return path;
}

// Render-time error boundary. A throw inside any descendant — e.g. a stale ref
// method, a WebGL context-loss race, an SDK update with a breaking change —
// would otherwise unmount the entire React root and leave the host page with
// nothing visible. Caught errors degrade to a fallback pill that opens the
// iframe-based embed page in a new tab so the customer keeps a path to book.
interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback: React.ReactNode;
}
class WidgetErrorBoundary extends React.Component<ErrorBoundaryProps, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }
  componentDidCatch(err: unknown, info: React.ErrorInfo): void {
    console.warn('[bookone-widget] render error — UI degraded:', err, info);
  }
  render(): React.ReactNode {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

interface FallbackPillProps {
  trigger: TriggerConfig | undefined;
  fallbackUrl: string;
  venueName: string;
}

// Fallback pill rendered when WidgetErrorBoundary catches. Same brand
// background + label as the real pill so the host page doesn't suddenly
// look broken; click opens the iframe-mode embed page in a new tab.
function FallbackPill({ trigger, fallbackUrl, venueName }: FallbackPillProps): React.ReactElement {
  return (
    <a
      href={fallbackUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Plan a private event at ${venueName}`}
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        height: 64,
        padding: '10px 20px 10px 20px',
        borderRadius: 999,
        background: trigger?.background ?? '#000000',
        color: trigger?.textColor ?? '#F5F0E6',
        textDecoration: 'none',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: 14,
        display: 'inline-flex',
        alignItems: 'center',
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.35)',
        zIndex: 9998,
      }}
    >
      {trigger?.label ?? 'Plan a private event'}
    </a>
  );
}

interface LocationOption {
  slug: string;
  name: string;
  neighborhood?: string;
  capacityHint?: string;
}

interface WidgetRenderArgs {
  venueId: string;
  venueName: string;
  agentId: string;
  logoUrl: string | null;
  menuUrl: string | undefined;
  supportedLanguages: SupportedLang[] | undefined;
  preset: BrandPreset;
  themeOverride: Partial<CateringWidgetTheme>;
  trigger: TriggerConfig | undefined;
  copy: CopyConfig | undefined;
  requireLocationPick: boolean;
  locations: LocationOption[] | undefined;
  eventGuideUrls: Record<string, string> | undefined;
}

class BookOneWidgetElement extends HTMLElement {
  private root: Root | null = null;
  private wrapper: HTMLDivElement | null = null;
  // trackingId is snapshotted once on construction so attribute mutations
  // (e.g. SPA route changes that re-set venue-id) don't re-issue ids and
  // detach mid-session bookings from their original consumer.
  private readonly trackingId: string = uuidv7();

  static get observedAttributes(): string[] {
    return [
      'venue-id',
      'theme',
      'agent-id',
      'venue-name',
      'logo-url',
      'menu-url',
      'languages',
      'locations',
      'event-guide-urls',
      'require-location-pick',
    ];
  }

  connectedCallback(): void {
    if (this.root) return;
    this.wrapper = document.createElement('div');
    this.appendChild(this.wrapper);
    this.root = createRoot(this.wrapper);
    this.render();
  }

  disconnectedCallback(): void {
    this.root?.unmount();
    this.root = null;
    this.wrapper?.remove();
    this.wrapper = null;
  }

  attributeChangedCallback(): void {
    if (this.root) this.render();
  }

  private parseLanguages(attr: string | null): SupportedLang[] | undefined {
    if (!attr) return undefined;
    return normalizeLanguageList(attr.split(','));
  }

  // Parse the locations attribute. Accepts JSON: '[{"slug":"...","name":"..."}]'.
  // Falls back to undefined on parse error so single-location venues aren't
  // affected by attribute typos.
  private parseLocations(attr: string | null): LocationOption[] | undefined {
    if (!attr) return undefined;
    try {
      const parsed = JSON.parse(attr);
      if (!Array.isArray(parsed)) return undefined;
      const locs: LocationOption[] = parsed
        .filter((p) => p && typeof p === 'object' && typeof p.slug === 'string' && typeof p.name === 'string')
        .map((p) => ({
          slug: p.slug,
          name: p.name,
          neighborhood: typeof p.neighborhood === 'string' ? p.neighborhood : undefined,
          capacityHint: typeof p.capacityHint === 'string' ? p.capacityHint : undefined,
        }));
      return locs.length > 0 ? locs : undefined;
    } catch {
      console.warn('[bookone-widget] failed to parse locations attribute (expected JSON array)');
      return undefined;
    }
  }

  // Parse the event-guide-urls attribute. Accepts JSON: '{"slug":"https://..."}'.
  // Returns undefined on parse error.
  private parseEventGuideUrls(attr: string | null): Record<string, string> | undefined {
    if (!attr) return undefined;
    try {
      const parsed = JSON.parse(attr);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined;
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v === 'string') out[k] = v;
      }
      return Object.keys(out).length > 0 ? out : undefined;
    } catch {
      console.warn('[bookone-widget] failed to parse event-guide-urls attribute (expected JSON object)');
      return undefined;
    }
  }

  private render(): void {
    const venueId = this.getAttribute('venue-id') ?? '';
    if (!venueId) {
      console.warn('[bookone-widget] missing venue-id attribute');
      return;
    }

    const themeAttr = this.getAttribute('theme') ?? 'default';
    const preset: BrandPreset = isBrandPreset(themeAttr) ? themeAttr : 'default';
    const themeOverride = BRAND_PRESETS[preset];
    const trigger = BRAND_TRIGGER_CONFIG[preset];
    const copy = BRAND_COPY[preset];

    const agentIdAttr = this.getAttribute('agent-id');
    const venueNameAttr = this.getAttribute('venue-name');
    const logoUrlAttr = this.getAttribute('logo-url');
    const menuUrlAttr = this.getAttribute('menu-url') ?? undefined;
    const supportedLanguages = this.parseLanguages(this.getAttribute('languages'));
    const locations = this.parseLocations(this.getAttribute('locations'));
    const eventGuideUrls = this.parseEventGuideUrls(this.getAttribute('event-guide-urls'));
    const requireLocationPick =
      this.getAttribute('require-location-pick') === 'true' && !!locations && locations.length > 0;

    if (agentIdAttr && venueNameAttr) {
      this.renderWidget({
        venueId,
        venueName: venueNameAttr,
        agentId: agentIdAttr,
        logoUrl: logoUrlAttr,
        menuUrl: menuUrlAttr,
        supportedLanguages,
        preset,
        themeOverride,
        trigger,
        copy,
        requireLocationPick,
        locations,
        eventGuideUrls,
      });
      return;
    }

    this.renderPlaceholder(trigger);
    this.fetchAndRender(
      venueId,
      preset,
      themeOverride,
      trigger,
      copy,
      menuUrlAttr,
      supportedLanguages,
      requireLocationPick,
      locations,
      eventGuideUrls,
    );
  }

  private renderPlaceholder(trigger: TriggerConfig | undefined): void {
    if (!this.root) return;
    this.root.render(
      <div
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          height: 64,
          padding: '10px 20px 10px 20px',
          borderRadius: 999,
          background: trigger?.background ?? '#000000',
          color: trigger?.textColor ?? '#F5F0E6',
          opacity: 0.6,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: 14,
          display: 'inline-flex',
          alignItems: 'center',
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.35)',
          zIndex: 9998,
        }}
      >
        Loading…
      </div>,
    );
  }

  private async fetchAndRender(
    venueId: string,
    preset: BrandPreset,
    themeOverride: Partial<CateringWidgetTheme>,
    trigger: TriggerConfig | undefined,
    copy: CopyConfig | undefined,
    menuUrlOverride: string | undefined,
    supportedLanguages: SupportedLang[] | undefined,
    requireLocationPickOverride: boolean,
    locationsOverride: LocationOption[] | undefined,
    eventGuideUrlsOverride: Record<string, string> | undefined,
  ): Promise<void> {
    try {
      const res = await fetch(`${API_BASE}/venues/${venueId}/profile`);
      if (!res.ok) throw new Error(`profile fetch returned ${res.status}`);
      const venue = await res.json();
      const cateringConfig = venue?.cateringConfig ?? {};
      const agentId: string | undefined = cateringConfig.widgetAgentId;
      const logoUrl: string | null =
        cateringConfig.widgetLogoUrl ?? venue?.heroImageUrl ?? null;
      // Mirror the embed-page resolution (apps/web/src/app/embed/[venueId]/page.tsx:81):
      // first menu's fileUrl. Caller-supplied menu-url attribute takes
      // precedence so a venue can override per install.
      const menuUrl = menuUrlOverride ?? venue?.menus?.[0]?.fileUrl ?? undefined;
      // Multi-location attributes — caller-supplied overrides take precedence;
      // otherwise read from cateringConfig server-side. Boxcar Social sets
      // requireLocationPick + per-location pricing/guides server-side so the
      // snippet attributes are optional sugar.
      const cateringLocations: LocationOption[] | undefined = Array.isArray(cateringConfig.locations)
        ? cateringConfig.locations
            .filter((l: unknown): l is { slug: string; name: string } =>
              !!l && typeof l === 'object' && typeof (l as Record<string, unknown>).slug === 'string'
              && typeof (l as Record<string, unknown>).name === 'string')
            .map((l: Record<string, unknown>) => ({
              slug: l.slug as string,
              name: l.name as string,
              neighborhood: typeof l.neighborhood === 'string' ? l.neighborhood : undefined,
              capacityHint: typeof l.capacityHint === 'string' ? l.capacityHint : undefined,
            }))
        : undefined;
      const locations = locationsOverride ?? cateringLocations;
      const eventGuideUrls = eventGuideUrlsOverride
        ?? (cateringConfig.eventGuideUrls && typeof cateringConfig.eventGuideUrls === 'object'
          ? cateringConfig.eventGuideUrls
          : undefined);
      const requireLocationPick =
        requireLocationPickOverride
        || (cateringConfig.requireLocationPick === true && !!locations && locations.length > 0);
      if (!agentId || !venue?.name) {
        console.warn('[bookone-widget] venue is not widget-enabled', venueId);
        return;
      }
      // Caller-supplied `languages` attribute wins; otherwise the venue's
      // catering_config.widgetLanguages drives the in-widget language toggle.
      // Without this fallback the minimal snippet (venue-id + theme only)
      // never showed the FR/EN picker — found on ottavio.ca, 2026-06-10.
      const languages = supportedLanguages ?? normalizeLanguageList(cateringConfig.widgetLanguages);
      this.renderWidget({
        venueId,
        venueName: venue.name,
        agentId,
        logoUrl,
        menuUrl,
        supportedLanguages: languages,
        preset,
        themeOverride,
        trigger,
        copy,
        requireLocationPick,
        locations,
        eventGuideUrls,
      });
    } catch (err) {
      console.warn('[bookone-widget] failed to fetch venue profile', err);
    }
  }

  private renderWidget(args: WidgetRenderArgs): void {
    if (!this.root) return;
    const {
      venueId,
      venueName,
      agentId,
      logoUrl,
      menuUrl,
      supportedLanguages,
      preset,
      themeOverride,
      trigger,
      copy,
      requireLocationPick,
      locations,
      eventGuideUrls,
    } = args;
    // Absolutize every venue/brand asset URL against bookoneparty.com. The host
    // page (e.g. ui-two-topaz.vercel.app) does not host these assets, so any
    // path that arrives here as '/foo.png' must be rewritten to
    // 'https://www.bookoneparty.com/foo.png' before React renders it.
    const absoluteLogoUrl = absolutize(logoUrl);
    const absoluteTriggerLogoUrl = absolutize(trigger?.logoUrl ?? logoUrl ?? '/favicon.ico');
    // Fallback URL for the error-boundary path: the iframe-mode embed page on
    // bookoneparty.com. It doesn't depend on our React tree so even if our bundle
    // is broken in some host-specific way, the customer still has a working
    // chat path through that iframe.
    const fallbackUrl = `${BOOKONE_ORIGIN}/embed/${venueId}?theme=${preset}`;
    this.root.render(
      <WidgetErrorBoundary
        fallback={
          <FallbackPill trigger={trigger} fallbackUrl={fallbackUrl} venueName={venueName} />
        }
      >
        <FloatingWidget
          agentId={agentId}
          venueId={venueId}
          venueName={venueName}
          trackingId={this.trackingId}
          menuUrl={menuUrl}
          theme={{ ...themeOverride, logoUrl: absoluteLogoUrl }}
          triggerLogoUrl={absoluteTriggerLogoUrl ?? `${BOOKONE_ORIGIN}/favicon.ico`}
          triggerBackground={trigger?.background ?? '#000000'}
          triggerLabel={trigger?.label ?? 'Plan a private event'}
          triggerTextColor={trigger?.textColor ?? '#F5F0E6'}
          triggerAriaLabel={trigger?.ariaLabel ?? `Plan a private event at ${venueName}`}
          // Bilingual venues (2+ widget languages) get NO copy override —
          // BRAND_COPY strings are single-language and would pin the title/
          // description to one language while the toggle switches everything
          // else (seen live on ottavio.ca). Undefined lets the widget render
          // its own localized defaults (widget.idle.title.default /
          // widget.idle.description — all 4 langs, parity-tested). Same rule
          // embed-client.tsx and book-client.tsx already apply.
          titleOverride={
            supportedLanguages && supportedLanguages.length > 1
              ? undefined
              : (copy?.title ?? `Plan a private event at ${venueName}`)
          }
          descriptionOverride={
            supportedLanguages && supportedLanguages.length > 1
              ? undefined
              : (copy?.description ?? 'Chat with our hostess — two-minute intake, the team follows up.')
          }
          logoHasOwnBorder
          supportedLanguages={supportedLanguages}
          ambienceAudioUrl={`${BOOKONE_ORIGIN}/audio/akiro-ambience.mp3`}
          appOrigin={BOOKONE_ORIGIN}
          requireLocationPick={requireLocationPick}
          locations={locations}
          eventGuideUrls={eventGuideUrls}
          // The bundle renders in the HOST page's DOM — if group links are
          // ever wired through this surface they must open the canonical
          // /book page as a full tab, never navigate the venue's own site.
          groupLinkTarget="_blank"
        />
      </WidgetErrorBoundary>,
    );
  }
}

// Inject a minimal style rule so the custom element doesn't break host layout.
// Without this, the browser's default `display: inline` for unknown elements
// could affect surrounding text flow if a venue pastes the snippet inside a
// paragraph. `display: contents` makes the element layout-neutral — its child
// (the React mount) determines its own positioning via `position: fixed`.
function injectHostStyle(): void {
  if (document.getElementById('bookone-widget-host-style')) return;
  const style = document.createElement('style');
  style.id = 'bookone-widget-host-style';
  style.textContent = 'bookone-widget{display:contents}';
  document.head.appendChild(style);
}

// Inject the same font <link> tags that apps/web/src/app/layout.tsx puts in
// BookOne's own <head>. The brand presets (membersonly, akiro, ottavio) all
// declare specific fontDisplay / fontBody families (Instrument Serif,
// General Sans, etc.) — without these stylesheets on the host page, the
// browser falls back to the next item in each family stack (Georgia / system
// sans), and the typography reads visibly different from the reference embed
// page. Idempotent via the sentinel id; per-link try/catch keeps the console
// clean if a host CSP blocks the external stylesheets.
function injectHostFonts(): void {
  if (document.getElementById('bookone-widget-host-fonts')) return;
  const links: Array<Partial<HTMLLinkElement>> = [
    { rel: 'preconnect', href: 'https://api.fontshare.com', crossOrigin: 'anonymous' },
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
    {
      rel: 'stylesheet',
      href: 'https://api.fontshare.com/v2/css?f[]=clash-display@200,300,400,500,600,700&f[]=general-sans@200,300,400,500,600,700&display=swap',
    },
    {
      rel: 'stylesheet',
      href: 'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=JetBrains+Mono:wght@400;500&display=swap',
    },
  ];
  links.forEach((attrs, i) => {
    try {
      const link = document.createElement('link');
      Object.assign(link, attrs);
      if (i === 0) link.id = 'bookone-widget-host-fonts';
      document.head.appendChild(link);
    } catch {
      // Host CSP or DOM-restricted environment — fail silent. System fonts
      // become the visible fallback.
    }
  });
}

if (typeof window !== 'undefined' && typeof customElements !== 'undefined') {
  injectHostStyle();
  injectHostFonts();
  if (!customElements.get('bookone-widget')) {
    customElements.define('bookone-widget', BookOneWidgetElement);
  }
}
